/**
 * OOXML Parser V2 — Entry Point
 *
 * Two-stage architecture:
 *   Stage 1: OOXML XML → Normalized IR (all styles resolved, raw units)
 *   Stage 2: IR → ProseMirror JSON (unit conversion, mark application)
 *
 * Usage:
 *   const result = await parseDocxV2IR(file);
 *   // result has same shape as V1's OoxmlParseResult
 */

import type { OoxmlParseResult } from "../ooxmlParser/types";
import { readDocxPackage } from "../ooxmlParser/zipReader";
import { buildDocumentIR } from "./stage1/buildDocumentIR";
import { irToProseMirror } from "./stage2/irToProseMirror";

export type { DocumentIR } from "./irTypes";

/**
 * Parse a DOCX file using the V2 IR-based pipeline.
 *
 * Returns the same OoxmlParseResult shape as V1, so it's a drop-in replacement.
 */
export async function parseDocxV2IR(
  file: ArrayBuffer | Blob | Uint8Array,
  options?: { generateNodeId?: () => string }
): Promise<OoxmlParseResult> {
  // Step 1: Unzip and parse XML (reuses V1's zipReader)
  const pkg = await readDocxPackage(file);

  // Step 2: Stage 1 — build normalized IR
  const { ir, ctx } = buildDocumentIR(pkg, options?.generateNodeId);

  // Step 3: Stage 2 — convert IR to ProseMirror JSON
  const result = await irToProseMirror(ir, ctx, pkg);

  return result;
}
